import { useMemo, useCallback } from "react";
import { SelectOptionType } from "@aidkitorg/component-library";
import { useSettings } from "../../Contexts/SettingsContext";
import { ComponentKind, FieldEntryDataType, TargetFieldCategoryValue } from "@aidkitorg/types/lib/admin-panel/settings";

export interface UseFieldOptionsConfig {
  /**
   * Filter function to include/exclude specific fields
   */
  fieldFilter?: (field: any) => boolean;
  /**
   * Custom label function - return display label for field
   */
  getLabelForField?: (field: any) => string;
  /**
   * Filter by component kind
   */
  componentKinds?: ComponentKind[];
  /**
   * Filter by data type
   */
  dataTypes?: FieldEntryDataType[];
}

export const useFieldOptions = (config: UseFieldOptionsConfig = {}) => {
  const { fields } = useSettings();
  
  const {
    fieldFilter = () => true,
    getLabelForField = (field) => field.displayName || field.id,
    componentKinds,
    dataTypes,
  } = config;

  // Create flat field options
  const fieldOptions: SelectOptionType[] = useMemo(() => {
    if (!fields || fields.length === 0) return [];

    // Combined filter function that includes type-based filtering
    const combinedFilter = (field: any): boolean => {
      // Apply custom field filter first
      if (!fieldFilter(field)) return false;

      // Filter by component kind
      if (componentKinds && componentKinds.length > 0) {
        if (!field.kind || !componentKinds.includes(field.kind)) return false;
      }

      // Filter by data type
      if (dataTypes && dataTypes.length > 0) {
        if (!field.dataType || !dataTypes.includes(field.dataType)) return false;
      }
      return true;
    };

    // Filter and map fields to flat options
    return fields
      .filter(combinedFilter)
      .map(field => ({
        value: field.id,
        label: getLabelForField(field)
      }));
  }, [fields, fieldFilter, getLabelForField, componentKinds, dataTypes]);

  // Helper to get selected field option
  const getSelectedFieldOption = useCallback((fieldId: string): SelectOptionType | undefined => {
    if (!fieldId || !fields) return undefined;
    
    const field = fields.find(f => f.id === fieldId);
    if (!field) return undefined;
    
    return {
      value: field.id,
      label: getLabelForField(field)
    };
  }, [fields, getLabelForField]);

  // Helper to get field by ID
  const getFieldById = useCallback((fieldId: string) => {
    return fields?.find(f => f.id === fieldId);
  }, [fields]);

  return {
    fieldOptions,
    getSelectedFieldOption,
    getFieldById,
    fields: fields || []
  };
};